import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Alert, CircularProgress, Typography, Button, Switch, FormControlLabel, Chip,
} from '@mui/material';
import { PageHeader } from '../../components/ui/PageHeader';
import { brandColors, brandCss } from '../../theme/appTheme';
import { catchyPrimaryButtonSx } from '../../theme/formStyles';
import { SuperAdminDataTable, apiErrorMessage } from '../../components/superadmin/SuperAdminDataTable';
import { normalizeImportRows } from '../../utils/exportToExcel';

const TOGGLE_IMPORT_COLUMNS = [
  { field: 'key', header: 'Key' },
  { field: 'enabled', header: 'Enabled' },
];

function parseToggleEnabled(value: unknown): boolean {
  const v = String(value ?? '').trim().toLowerCase();
  return v === 'true' || v === 'yes' || v === '1' || v === 'enabled' || v === 'on';
}

interface ToggleItem {
  key: string;
  label: string;
  description?: string;
}

interface ToggleRow extends Record<string, unknown> {
  key: string;
  label: string;
  description?: string;
  enabled: boolean;
}

interface SuperAdminTogglePageProps {
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  breadcrumb: string;
  load: () => Promise<Record<string, boolean>>;
  save: (data: Record<string, boolean>) => Promise<void>;
  items: ToggleItem[];
  enableImport?: boolean;
  exportFileName?: string;
  infoBelow?: React.ReactNode;
}

export const SuperAdminTogglePage: React.FC<SuperAdminTogglePageProps> = ({
  title, subtitle, icon, breadcrumb, load, save, items,
  enableImport = true,
  exportFileName,
  infoBelow,
}) => {
  const [data, setData] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await load();
      setData(result || {});
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load settings'));
    } finally {
      setLoading(false);
    }
  }, [load]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggle = (key: string) => setData((d) => ({ ...d, [key]: !d[key] }));

  const handleSave = async () => {
    try {
      await save(data);
      setMessage('Saved successfully');
      setError('');
    } catch (err) {
      setError(apiErrorMessage(err, 'Save failed'));
    }
  };

  const handleImport = useCallback((records: Record<string, unknown>[]) => {
    const normalized = normalizeImportRows(records, TOGGLE_IMPORT_COLUMNS);
    if (!normalized.length) return;
    setData((prev) => {
      const next = { ...prev };
      normalized.forEach((row) => {
        const key = String(row.key || row.Key || '').trim();
        if (!key || !items.some((i) => i.key === key)) return;
        next[key] = parseToggleEnabled(row.enabled ?? row.Enabled);
      });
      return next;
    });
    setMessage(`Imported ${normalized.length} row(s) — review and save`);
  }, [items]);

  const rows: ToggleRow[] = useMemo(() => items.map((item) => ({
    ...item,
    enabled: data[item.key] !== false,
  })), [items, data]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress color="success" />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader title={title} subtitle={subtitle} icon={icon}
        action={<Button variant="contained" onClick={handleSave} sx={catchyPrimaryButtonSx}>Save Changes</Button>}
      />
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}
      {infoBelow}

      <SuperAdminDataTable<ToggleRow>
        rows={rows}
        searchPlaceholder="Search..."
        recordLabel="items"
        exportFileName={exportFileName || `super-admin-${breadcrumb.toLowerCase().replace(/\s+/g, '-')}`}
        exportSheetName={breadcrumb}
        enableImport={enableImport}
        onImport={handleImport}
        exportExtraColumns={[{ id: 'key', header: 'Key', exportValue: (row) => row.key }]}
        statusOptions={[
          { label: 'All', value: 'all' },
          { label: 'Enabled', value: 'true' },
          { label: 'Disabled', value: 'false' },
        ]}
        getStatus={(row) => (row.enabled ? 'true' : 'false')}
        columns={[
          {
            id: 'label',
            header: 'Feature',
            searchValue: (row) => `${row.label} ${row.description || ''}`,
            exportValue: (row) => row.label,
            render: (row) => (
              <Box>
                <Typography fontWeight={700} fontSize={13}>{row.label}</Typography>
                {row.description && (
                  <Typography fontSize={13} color="textSecondary">{row.description}</Typography>
                )}
              </Box>
            ),
          },
          {
            id: 'status',
            header: 'Status',
            exportValue: (row) => row.enabled ? 'Enabled' : 'Disabled',
            filterType: 'select',
            filterOptions: [{ label: 'Enabled', value: 'true' }, { label: 'Disabled', value: 'false' }],
            filterValue: (row) => String(row.enabled),
            render: (row) => (
              <Chip
                label={row.enabled ? 'Enabled' : 'Disabled'}
                size="small"
                sx={{
                  fontWeight: 700,
                  bgcolor: row.enabled ? brandCss.green100 : '#f1f5f9',
                  color: row.enabled ? brandCss.primaryDark : brandColors.textMuted,
                }}
              />
            ),
          },
        ]}
        renderActions={(row) => (
          <FormControlLabel
            control={<Switch checked={!!data[row.key]} onChange={() => toggle(row.key)} color="success" size="small" />}
            label=""
            sx={{ m: 0 }}
          />
        )}
      />
    </Box>
  );
};
